import React, { useEffect, useMemo, useState } from "react";
import { X, FileText, Download, Trash2, CheckCircle, RefreshCw, Clock, BarChart3 } from "lucide-react";
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';

/**
 * FullFinalSettlement.jsx
 *
 * Fully functional Full & Final (F&F) Settlement Engine (JSX)
 * - Standard Indian gratuity formula implemented (option A)
 * - Roboto + Poppins font stack applied
 * - LocalStorage-backed settlement history
 * - Clean Zoho/Keka-like UI with right-side drawer
 *
 * Save as: src/pages/FullFinalSettlement.jsx
 */

/* -------------------- Mock Employees (replaceable with API) -------------------- */
const MOCK_EMPLOYEES = [
  {
    id: "E1001",
    name: "Aman Kapoor",
    designation: "Senior Developer",
    department: "Engineering",
    joinDate: "2015-02-15",
    basic: 60000,
    grossMonthly: 90000,
    leaveBalance: 12.5, // days
    outstandingLoan: 25000, // INR
    assignedAssets: [{ id: "LAP-01", name: "Laptop - Dell" }],
  },
  {
    id: "E1002",
    name: "Priya Nair",
    designation: "Product Manager",
    department: "Product",
    joinDate: "2018-08-01",
    basic: 45000,
    grossMonthly: 70000,
    leaveBalance: 5,
    outstandingLoan: 0,
    assignedAssets: [],
  },
  {
    id: "E1003",
    name: "Ramesh Kumar",
    designation: "HR Executive",
    department: "HR",
    joinDate: "2021-06-10",
    basic: 22000,
    grossMonthly: 30000,
    leaveBalance: 2,
    outstandingLoan: 5000,
    assignedAssets: [{ id: "MOB-21", name: "Mobile - iPhone" }],
  },
];

/* -------------------- Utility Functions -------------------- */
const daysBetween = (d1, d2) => {
  const a = new Date(d1);
  const b = new Date(d2);
  // normalize
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
  return diff;
};

const yearsOfService = (joinDate, lastWorkingDate) => {
  const j = new Date(joinDate);
  const l = new Date(lastWorkingDate);
  // calculate full years (with fraction)
  const years = (l - j) / (1000 * 60 * 60 * 24 * 365.25);
  return Math.floor(years * 100) / 100; // 2 decimal precision
};

const formatINR = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "₹0";
  return `₹${Number(n).toLocaleString("en-IN")}`;
};

const toCSV = (obj) => {
  // simple flat CSV converter for settlement object
  const rows = [];
  const headers = Object.keys(obj).filter(k => typeof obj[k] !== "object");
  rows.push(headers.join(","));
  rows.push(headers.map(h => `"${String(obj[h] ?? "")}"`).join(","));
  return rows.join("\n");
};

/* -------------------- Crux Calculations -------------------- */

// Gratuity (Standard Indian formula)
function computeGratuity(lastDrawnBasicMonthly, yearsOfServiceVal) {
  // Eligibility: at least 5 years
  if (yearsOfServiceVal < 5) return 0;
  // Gratuity = (15 * lastDrawnBasic * yearsOfService) / 26
  const g = (15 * Number(lastDrawnBasicMonthly) * Math.floor(yearsOfServiceVal)) / 26;
  return Math.round(g * 100) / 100;
}

// Leave encashment: Basic / 26 * leaveDays
function computeLeaveEncashment(basicMonthly, leaveDays) {
  const encash = (Number(basicMonthly) / 26) * Number(leaveDays || 0);
  return Math.round(encash * 100) / 100;
}

// Pro-rated salary: basic * (workedDays / monthDays) -> we assume 30-day month for payroll conventions
function computeProratedSalary(basicMonthly, lastWorkingDayInMonth) {
  // lastWorkingDayInMonth: date object or day number within month
  if (!lastWorkingDayInMonth) return 0;
  const day = typeof lastWorkingDayInMonth === "number" ? lastWorkingDayInMonth : new Date(lastWorkingDayInMonth).getDate();
  const worked = Math.max(0, Math.min(30, day));
  const prorated = (Number(basicMonthly) * (worked / 30));
  return Math.round(prorated * 100) / 100;
}

// Notice recovery: if noticeRequired > noticeServed => recover basic/30 * (required-served)
function computeNoticeRecovery(basicMonthly, noticeRequiredDays = 0, noticeServedDays = 0) {
  const shortfall = Math.max(0, Number(noticeRequiredDays || 0) - Number(noticeServedDays || 0));
  const recovery = (Number(basicMonthly) / 30) * shortfall;
  return Math.round(recovery * 100) / 100;
}

/* -------------------- LocalStorage Helpers -------------------- */
const LS_KEY = "ff_settlements_v1";

function loadHistory() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    return [];
  }
}
function saveHistory(arr) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(arr));
  } catch (e) {}
}

/* -------------------- Component -------------------- */
export default function FullFinalSettlement() {
  const [employees, setEmployees] = useState(MOCK_EMPLOYEES);
  const [history, setHistory] = useState(() => loadHistory());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [current, setCurrent] = useState(null); // settlement in-progress object
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, settled, rolledback
  const [activeTab, setActiveTab] = useState('settlement');
  
  // Full Final Settlement specific activity types
  const activityTypes = [
    "Settlement Started", "Settlement Completed", "Settlement Rolled Back", 
    "Gratuity Calculated", "Leave Encashed", "Asset Recovery Processed"
  ];

  // Full Final Settlement specific report types
  const reportTypes = [
    "Full Final Settlement Report", "Gratuity Calculation Report", 
    "Leave Encashment Report", "Asset Recovery Report",
    "Settlement Summary Report", "Employee Exit Report"
  ];

  useEffect(() => {
    saveHistory(history);
  }, [history]);

  // open settlement drawer for an employee
  function startSettlement(emp) {
    const today = new Date();
    const defaultLastWorking = new Date().toISOString().slice(0, 10);
    const init = {
      id: `sett_${emp.id}_${Date.now()}`,
      employee: emp,
      lastWorkingDay: defaultLastWorking,
      noticeRequiredDays: 30,
      noticeServedDays: 0,
      leaveBalanceAtExit: Number(emp.leaveBalance || 0),
      leaveEncashDays: 0,
      outstandingLoan: Number(emp.outstandingLoan || 0),
      assetRecoveries: emp.assignedAssets ? emp.assignedAssets.map(a => ({ ...a, toRecover: false, amount: 0 })) : [],
      customAdditions: [],
      customDeductions: [],
      manualAdjustments: 0,
      createdAt: new Date().toISOString(),
      status: "in-progress", // in-progress, completed, rolledback
      notes: "",
    };
    setCurrent(init);
    setDrawerOpen(true);
  }

  // update current field
  function updateCurrent(path, val) {
    setCurrent(prev => ({ ...prev, [path]: val }));
  }

  // add custom item
  function addCustomItem(kind) {
    setCurrent(prev => ({ ...prev, [kind === "add" ? "customAdditions" : "customDeductions"]: [...(prev[kind === "add" ? "customAdditions" : "customDeductions"] || []), { id: `c_${Date.now()}`, label: "", amount: 0 }] }));
  }
  function updateCustomItem(kind, idx, field, value) {
    setCurrent(prev => {
      const arrKey = kind === "add" ? "customAdditions" : "customDeductions";
      const copy = [...(prev[arrKey] || [])];
      copy[idx] = { ...copy[idx], [field]: value };
      return { ...prev, [arrKey]: copy };
    });
  }
  function removeCustomItem(kind, idx) {
    setCurrent(prev => {
      const arrKey = kind === "add" ? "customAdditions" : "customDeductions";
      const copy = [...(prev[arrKey] || [])].filter((_, i) => i !== idx);
      return { ...prev, [arrKey]: copy };
    });
  }

  // toggle asset recovery
  function toggleAssetRecovery(index) {
    setCurrent(prev => {
      const copy = [...(prev.assetRecoveries || [])];
      copy[index].toRecover = !copy[index].toRecover;
      if (!copy[index].toRecover) copy[index].amount = 0;
      return { ...prev, assetRecoveries: copy };
    });
  }
  function updateAssetRecoveryAmount(index, amount) {
    setCurrent(prev => {
      const copy = [...(prev.assetRecoveries || [])];
      copy[index].amount = Number(amount || 0);
      return { ...prev, assetRecoveries: copy };
    });
  }

  // computed summary (memoized)
  const summary = useMemo(() => {
    if (!current) return null;
    const emp = current.employee;
    const lw = new Date(current.lastWorkingDay);
    // years of service
    const yrs = yearsOfService(emp.joinDate, current.lastWorkingDay);
    const gratuity = computeGratuity(emp.basic, yrs);
    // leave encashment (we'll use leaveEncashDays if set, else full leaveBalanceAtExit)
    const leaveDays = current.leaveEncashDays > 0 ? Number(current.leaveEncashDays) : Number(current.leaveBalanceAtExit || 0);
    const leaveEncash = computeLeaveEncashment(emp.basic, leaveDays);
    // prorated salary
    const lastDay = lw.getDate();
    const prorated = computeProratedSalary(emp.basic, lastDay);
    // notice recovery
    const noticeRecovery = computeNoticeRecovery(emp.basic, Number(current.noticeRequiredDays || 0), Number(current.noticeServedDays || 0));
    // asset recoveries
    const assetRecoveriesTotal = (current.assetRecoveries || []).reduce((s, a) => s + (a.toRecover ? Number(a.amount || 0) : 0), 0);
    // loan recoveries
    const loanRecovery = Number(current.outstandingLoan || 0);
    // custom additions / deductions
    const additionsTotal = (current.customAdditions || []).reduce((s, a) => s + Number(a.amount || 0), 0);
    const deductionsTotal = (current.customDeductions || []).reduce((s, d) => s + Number(d.amount || 0), 0);

    // gross payable = prorated salary + leave encash + gratuity + additions
    const grossPayable = prorated + leaveEncash + gratuity + additionsTotal + Number(current.manualAdjustments || 0);
    // total recoveries = notice + asset + loan + deductions
    const totalRecoveries = noticeRecovery + assetRecoveriesTotal + loanRecovery + deductionsTotal;
    // net settlement
    const netPayable = Math.round((grossPayable - totalRecoveries) * 100) / 100;

    return {
      yrs,
      gratuity,
      leaveEncash,
      prorated,
      noticeRecovery,
      assetRecoveriesTotal,
      loanRecovery,
      additionsTotal,
      deductionsTotal,
      grossPayable,
      totalRecoveries,
      netPayable,
    };
  }, [current]);

  // finalize settlement (save to history)
  function finalizeSettlement() {
    if (!current) return;
    if (!window.confirm("Finalize settlement? This will mark settlement as completed and store it in history.")) return;
    const settled = { ...current, finalizedAt: new Date().toISOString(), status: "completed", summary: summary || {} };
    setHistory(prev => [settled, ...prev]);
    setDrawerOpen(false);
    setCurrent(null);
    alert("Settlement finalized and saved to history.");
  }

  // rollback from history (mark rolledback)
  function rollbackHistory(id) {
    if (!window.confirm("Mark this settlement as rolled back? This will not delete record but mark as rolledback.")) return;
    setHistory(prev => prev.map(h => (h.id === id ? { ...h, status: "rolledback", rolledBackAt: new Date().toISOString() } : h)));
    alert("Settlement marked as rolled back.");
  }

  // delete history item
  function deleteHistoryItem(id) {
    if (!window.confirm("Delete this settlement history item permanently?")) return;
    setHistory(prev => prev.filter(h => h.id !== id));
  }

  // export settlement JSON / CSV
  function exportSettlementJSON(id) {
    const item = history.find(h => h.id === id);
    if (!item) return;
    const blob = new Blob([JSON.stringify(item, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `settlement_${item.employee.id}_${item.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  function exportSettlementCSV(id) {
    const item = history.find(h => h.id === id);
    if (!item) return;
    // create a simple csv: label,value rows
    const rows = [
      ["employeeId", item.employee.id],
      ["employeeName", item.employee.name],
      ["designation", item.employee.designation],
      ["department", item.employee.department],
      ["joinDate", item.employee.joinDate],
      ["lastWorkingDay", item.lastWorkingDay],
      ["yearsOfService", (item.summary?.yrs ?? "")],
      ["gratuity", (item.summary?.gratuity ?? "")],
      ["leaveEncashment", (item.summary?.leaveEncash ?? "")],
      ["proratedSalary", (item.summary?.prorated ?? "")],
      ["grossPayable", (item.summary?.grossPayable ?? "")],
      ["totalRecoveries", (item.summary?.totalRecoveries ?? "")],
      ["netPayable", (item.summary?.netPayable ?? "")],
    ];
    const csv = rows.map(r => `${r[0]},${JSON.stringify(r[1])}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `settlement_${item.employee.id}_${item.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // quick search filter
  const filteredEmployees = employees.filter(e => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (e.name + " " + e.id + " " + e.designation).toLowerCase().includes(q);
  });

  const filteredHistory = history.filter(h => {
    if (filter === "all") return true;
    return h.status === filter;
  });

  // UI render
  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Full & Final Settlement Engine</h1>
            <p className="text-sm text-gray-500 mt-1">Process exits, compute gratuity, leave encash, recoveries and generate final payout in a structured flow.</p>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setActiveTab('activity')} 
              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <Clock size={14} /> Activity Logs
            </button>
            
            <button 
              onClick={() => setActiveTab('reports')} 
              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                activeTab === 'reports' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <BarChart3 size={14} /> Reports
            </button>
            
            <button 
              onClick={() => setActiveTab('settlement')} 
              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                activeTab === 'settlement' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <FileText size={14} /> Settlement
            </button>
            
            <button
              onClick={() => { setHistory([]); saveHistory([]); alert("Cleared history (demo)."); }}
              className="px-3 py-1 text-sm border rounded"
              title="Clear demo history"
            >
              Clear History
            </button>
            <button
              onClick={() => { setEmployees(MOCK_EMPLOYEES); alert("Reset employees (demo)."); }}
              className="px-3 py-1 text-sm border rounded"
              title="Reset employees demo"
            >
              Reset Employees
            </button>
          </div>
        </header>

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <ActivityLogs 
            moduleName="Full Final Settlement" 
            activityTypes={activityTypes} 
          />
        )}
        
        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <Reports 
            moduleName="Full Final Settlement" 
            reportTypes={reportTypes} 
          />
        )}

        {/* Settlement Tab */}
        {activeTab === 'settlement' && (
          <>
        {/* Employees & Search */}
        <div className="bg-white p-4 rounded shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <input placeholder="Search employee name, id or role..." value={search} onChange={(e) => setSearch(e.target.value)} className="border rounded p-2 w-72 text-sm" />
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border rounded p-2 text-sm">
                <option value="all">All History</option>
                <option value="completed">Settled</option>
                <option value="rolledback">Rolled back</option>
                <option value="in-progress">In progress</option>
              </select>
            </div>

            <div className="text-sm text-gray-500">Employees: {employees.length}</div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">Employee</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Join Date</th>
                  <th className="px-4 py-3">Basic</th>
                  <th className="px-4 py-3">Leave Bal</th>
                  <th className="px-4 py-3">Loan</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map(emp => (
                  <tr key={emp.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{emp.name}</div>
                      <div className="text-xs text-gray-400">{emp.id}</div>
                    </td>
                    <td className="px-4 py-3 text-center">{emp.designation}</td>
                    <td className="px-4 py-3 text-center">{emp.joinDate}</td>
                    <td className="px-4 py-3 text-center">{formatINR(emp.basic)}</td>
                    <td className="px-4 py-3 text-center">{emp.leaveBalance} days</td>
                    <td className="px-4 py-3 text-center">{formatINR(emp.outstandingLoan)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => startSettlement(emp)} className="px-3 py-1 bg-teal-600 text-white rounded text-sm">Start Settlement</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* History */}
        <div className="bg-white p-4 rounded shadow">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Settlement History</h3>
            <div className="text-xs text-gray-500">Saved local (demo)</div>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="text-gray-500 italic p-6">No settlement history available.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b">
                <tr>
                  <th className="py-2 text-left">Employee</th>
                  <th className="py-2">Date</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Net Pay</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map(h => (
                  <tr key={h.id} className="border-b hover:bg-gray-50">
                    <td className="py-2">
                      <div className="font-medium">{h.employee.name}</div>
                      <div className="text-xs text-gray-400">{h.employee.id}</div>
                    </td>
                    <td className="py-2 text-center">{new Date(h.finalizedAt || h.createdAt).toLocaleString()}</td>
                    <td className="py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium 
                        ${h.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          h.status === 'rolledback' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                        {h.status}
                      </span>
                    </td>
                    <td className="py-2 text-center">{formatINR(h.summary?.netPayable)}</td>
                    <td className="py-2 text-right space-x-2">
                      <button title="Export JSON" onClick={() => exportSettlementJSON(h.id)} className="px-2 py-1 border rounded text-sm hover:bg-gray-100"><Download size={14} /></button>
                      <button title="Export CSV" onClick={() => exportSettlementCSV(h.id)} className="px-2 py-1 border rounded text-sm hover:bg-gray-100"><FileText size={14} /></button>
                      {h.status !== "rolledback" ? (
                        <button onClick={() => rollbackHistory(h.id)} className="px-2 py-1 border rounded text-sm text-orange-600 hover:bg-orange-50"><RefreshCw size={14} /></button>
                      ) : (
                        <span className="text-xs text-gray-400 px-2 py-1">Rolled back</span>
                      )}
                      <button onClick={() => deleteHistoryItem(h.id)} className="px-2 py-1 border rounded text-sm text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
          </>
        )}
      </div>

      {/* Settlement Drawer */}
      {drawerOpen && current && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={() => { setDrawerOpen(false); setCurrent(null); }} />
          <aside className="fixed right-0 top-0 h-full w-full md:w-2/5 bg-white z-50 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">Settlement — {current.employee.name}</h2>
                <div className="text-xs text-gray-400">{current.employee.designation} • {current.employee.department}</div>
              </div>
              <button onClick={() => { setDrawerOpen(false); setCurrent(null); }} className="p-2 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* Basic details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Last Working Day</label>
                  <input type="date" value={current.lastWorkingDay} onChange={(e) => updateCurrent("lastWorkingDay", e.target.value)} className="mt-1 w-full border rounded p-2 text-sm" />
                </div>

                <div>
                  <label className="text-xs text-gray-600">Notice Period Required (days)</label>
                  <input type="number" value={current.noticeRequiredDays} onChange={(e) => updateCurrent("noticeRequiredDays", Number(e.target.value))} className="mt-1 w-full border rounded p-2 text-sm" />
                </div>

                <div>
                  <label className="text-xs text-gray-600">Notice Period Served (days)</label>
                  <input type="number" value={current.noticeServedDays} onChange={(e) => updateCurrent("noticeServedDays", Number(e.target.value))} className="mt-1 w-full border rounded p-2 text-sm" />
                </div>

                <div>
                  <label className="text-xs text-gray-600">Leave Balance at Exit (days)</label>
                  <input type="number" value={current.leaveBalanceAtExit} onChange={(e) => updateCurrent("leaveBalanceAtExit", Number(e.target.value))} className="mt-1 w-full border rounded p-2 text-sm" />
                </div>
              </div>

              {/* Leave encash */}
              <div className="border rounded p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Leave Encashment</div>
                    <div className="text-xs text-gray-500">Encashment calculated using Basic / 26</div>
                  </div>
                  <div className="text-sm font-semibold">{formatINR(summary?.leaveEncash)}</div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600">Encash Days (use 0 for auto = full balance)</label>
                    <input type="number" value={current.leaveEncashDays || ""} onChange={(e) => updateCurrent("leaveEncashDays", e.target.value === "" ? 0 : Number(e.target.value))} className="mt-1 w-full border rounded p-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Encash Amount</label>
                    <input type="text" value={formatINR(summary?.leaveEncash)} readOnly className="mt-1 w-full border rounded p-2 text-sm bg-white" />
                  </div>
                </div>
              </div>

              {/* Gratuity */}
              <div className="border rounded p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Gratuity (Standard Indian Formula)</div>
                    <div className="text-xs text-gray-500">Eligible after 5 years of service</div>
                    <div className="text-xs text-gray-400">Years of Service: {summary?.yrs ?? "-"}</div>
                  </div>
                  <div className="text-sm font-semibold">{formatINR(summary?.gratuity)}</div>
                </div>
              </div>

              {/* Prorated salary */}
              <div className="border rounded p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Pro-rated Salary</div>
                    <div className="text-xs text-gray-500">Calculated for last working month</div>
                  </div>
                  <div className="text-sm font-semibold">{formatINR(summary?.prorated)}</div>
                </div>
              </div>

              {/* Recoveries */}
              <div className="border rounded p-3">
                <div className="text-sm font-medium mb-2">Recoveries</div>

                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-600">Notice Recovery</div>
                    <div className="font-medium text-red-600">{formatINR(summary?.noticeRecovery)}</div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs text-gray-600">Asset Recoveries</div>
                    {(current.assetRecoveries || []).map((a, i) => (
                      <div key={a.id} className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-2 w-2/3">
                          <input type="checkbox" checked={a.toRecover} onChange={() => toggleAssetRecovery(i)} />
                          <span className="text-sm">{a.name}</span>
                        </label>
                        <input type="number" value={a.amount || ""} onChange={(e) => updateAssetRecoveryAmount(i, e.target.value)} className="w-1/3 border rounded p-1 text-sm" placeholder="amount" />
                      </div>
                    ))}
                    <div className="text-xs text-gray-400">Total asset recovery: {formatINR(summary?.assetRecoveriesTotal)}</div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-600">Loan Recovery</div>
                    <div className="font-medium text-red-600">{formatINR(summary?.loanRecovery)}</div>
                  </div>
                </div>
              </div>

              {/* Custom additions & deductions */}
              <div className="border rounded p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Custom Additions</div>
                  <button type="button" onClick={() => addCustomItem("add")} className="text-sm px-2 py-1 border rounded bg-white hover:bg-gray-100">Add</button>
                </div>
                <div className="mt-2 space-y-2">
                  {(current.customAdditions || []).map((c, i) => (
                    <div key={c.id} className="flex gap-2">
                      <input value={c.label} onChange={(e) => updateCustomItem("add", i, "label", e.target.value)} placeholder="Label" className="flex-1 border rounded p-2 text-sm" />
                      <input type="number" value={c.amount} onChange={(e) => updateCustomItem("add", i, "amount", Number(e.target.value || 0))} placeholder="Amount" className="w-36 border rounded p-2 text-sm" />
                      <button type="button" onClick={() => removeCustomItem("add", i)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <div className="text-xs text-gray-400">Additions total: {formatINR(summary?.additionsTotal)}</div>
                </div>
              </div>

              <div className="border rounded p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Custom Deductions</div>
                  <button type="button" onClick={() => addCustomItem("ded")} className="text-sm px-2 py-1 border rounded bg-white hover:bg-gray-100">Add</button>
                </div>
                <div className="mt-2 space-y-2">
                  {(current.customDeductions || []).map((c, i) => (
                    <div key={c.id} className="flex gap-2">
                      <input value={c.label} onChange={(e) => updateCustomItem("ded", i, "label", e.target.value)} placeholder="Label" className="flex-1 border rounded p-2 text-sm" />
                      <input type="number" value={c.amount} onChange={(e) => updateCustomItem("ded", i, "amount", Number(e.target.value || 0))} placeholder="Amount" className="w-36 border rounded p-2 text-sm" />
                      <button type="button" onClick={() => removeCustomItem("ded", i)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <div className="text-xs text-gray-400">Deductions total: {formatINR(summary?.deductionsTotal)}</div>
                </div>
              </div>

              {/* Manual adjustments */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Manual Adjustment (add + / deduct -)</label>
                  <input type="number" value={current.manualAdjustments || 0} onChange={(e) => updateCurrent("manualAdjustments", Number(e.target.value || 0))} className="mt-1 w-full border rounded p-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Notes</label>
                  <input type="text" value={current.notes || ""} onChange={(e) => updateCurrent("notes", e.target.value)} className="mt-1 w-full border rounded p-2 text-sm" />
                </div>
              </div>

              {/* Summary */}
              <div className="border rounded p-4 bg-teal-50">
                <h3 className="text-lg font-bold text-teal-800 mb-2">Final Settlement Summary</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-sm text-gray-700">Gross Payable (Total Income)</div>
                  <div className="text-right font-semibold text-teal-800">{formatINR(summary?.grossPayable)}</div>

                  <div className="text-sm text-gray-700">Total Recoveries (Deductions)</div>
                  <div className="text-right font-semibold text-red-700">{formatINR(summary?.totalRecoveries)}</div>

                  <div className="col-span-2 border-t mt-2 pt-2"></div>

                  <div className="text-base font-bold text-gray-900">Net Settlement Payable</div>
                  <div className="text-right text-xl font-extrabold text-indigo-700">{formatINR(summary?.netPayable)}</div>
                </div>
              </div>

              {/* Final actions */}
              <div className="flex items-center gap-3">
                <button type="button" onClick={finalizeSettlement} className="px-4 py-2 bg-teal-600 text-white rounded font-medium shadow-md hover:bg-teal-700 transition">
                  <CheckCircle size={18} className="inline mr-2" /> Finalize & Save
                </button>
                <button 
                  type="button" 
                  onClick={() => { 
                    const data = { ...current, summary: summary || {} }; // Include summary in the copied data
                    navigator.clipboard?.writeText(JSON.stringify(data, null, 2)); 
                    alert("Settlement JSON copied to clipboard"); 
                  }} 
                  className="px-3 py-2 border rounded font-medium hover:bg-gray-100 transition"
                >
                  Copy JSON
                </button>
                <button 
                  type="button" 
                  onClick={() => { 
                    // This is the fixed block that was incomplete:
                    if (!current) return;
                    const item = { ...current, summary: summary || {} };
                    const csvData = toCSV(item);
                    const blob = new Blob([csvData], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `settlement_draft_${item.employee.id}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    alert("Draft CSV downloaded.");
                  }} 
                  className="px-3 py-2 border rounded font-medium hover:bg-gray-100 transition"
                >
                  Download Draft CSV
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};